import { Auth } from '@angular/fire/auth';
import { createApi } from '@core/api/base-api';
import { HttpParams } from '@angular/common/http';
import {
  CreateNotificationPayload,
  Notification,
  PaginatedNotification,
} from '@core/interfaces/api';

export interface PaginationParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

export const createNotificationsApi = (auth: Auth) => {
  const api = createApi(auth);

  return {
    getNotifications: async (
      siteId: number,
      params: PaginationParams,
    ): Promise<PaginatedNotification> => {
      let queryParams = new HttpParams()
        .set('offset', params.page.toString())
        .set('limit', params.pageSize.toString());

      if (params.sortField) {
        queryParams = queryParams
          .set('sortField', params.sortField)
          .set('sortOrder', params.sortOrder || 'asc');
      }

      if (params.search?.trim()) {
        queryParams = queryParams
          .set('search', params.search)
          .set('searchBy', JSON.stringify(['title', 'text']));
      }

      return api.get<PaginatedNotification>(`/notifications/site/${siteId}`, {
        params: queryParams,
      });
    },

    getNotificationById: (id: number): Promise<Notification> =>
      api.get<Notification>(`/notifications/${id}`),

    createNotification: (
      payload: CreateNotificationPayload,
    ): Promise<Notification> =>
      api.post<Notification>(`/notifications/sites/send-notification`, payload),

    updateNotification: (
      id: number,
      payload: Partial<CreateNotificationPayload>,
    ): Promise<Notification> => api.post<Notification>(`/notifications/${id}`, payload),

    deleteNotification: (id: number): Promise<null> =>
      api.put<null>(`/notifications/${id}`),
  };
};

// Angular compatibility layer (optional parity with other features)
export class NotificationsApi {
  private readonly api: ReturnType<typeof createNotificationsApi>;

  constructor(private auth: Auth) {
    this.api = createNotificationsApi(auth);
  }

  getNotifications(
    siteId: number,
    params: PaginationParams,
  ): Promise<PaginatedNotification> {
    return this.api.getNotifications(siteId, params);
  }

  getNotificationById(id: number): Promise<Notification> {
    return this.api.getNotificationById(id);
  }

  createNotification(payload: CreateNotificationPayload): Promise<Notification> {
    return this.api.createNotification(payload);
  }

  updateNotification(
    id: number,
    payload: Partial<CreateNotificationPayload>,
  ): Promise<Notification> {
    return this.api.updateNotification(id, payload);
  }

  deleteNotification(id: number): Promise<null> {
    return this.api.deleteNotification(id);
  }
}

